import React from 'react'

import {
  HeadingLarge,
  HeadingMedium,
  PreHeading,
  BodyText,
  MetaText,
  RatingStars,
  RadioButtonInput,
} from '@pb/components'
import {
  isArketBrand,
  isCosBrand,
  isOtherStoriesBrand,
  isMonkiBrand,
  getLabelFromStorage,
} from '@pb/utils'
import cn from 'classnames'

import styles from '../ProductDetail.scss'
import { ProductAttribute } from '../types'

const renderRatingRadioList = (
  rating,
  handleRatingClick,
  list,
  reverseOrder,
): JSX.Element | null => {
  if (list.length > 0) {
    const radioButtons = list.map((value) => {
      const isOptionSelected = value.rating === Number(rating)
      const ratingLabel = value.label.split(' ')
      let capitalizedLabel = ''

      if (ratingLabel.length > 1) {
        capitalizedLabel = `${ratingLabel[0].charAt(0).toUpperCase()}${ratingLabel[0]
          .slice(1)
          .toLowerCase()} ${ratingLabel.slice(1).join(' ').toLowerCase()}`
      } else {
        capitalizedLabel = ratingLabel[0].toLowerCase()
        capitalizedLabel = `${capitalizedLabel.charAt(0).toUpperCase()}${capitalizedLabel.slice(1)}`
      }
      const label = `${value.rating}. ${capitalizedLabel}`
      return (
        <RadioButtonInput
          id={value.rating}
          key={value.rating}
          inputValue={value.rating}
          name={value.label}
          label={label}
          isRadioButtonIconLeft
          value={value.rating}
          onChange={handleRatingClick}
          checked={isOptionSelected}
          className={cn(styles.radioButtonField)}
          isRadioButtonFill={isCosBrand ? 'small' : 'normal'}
        />
      )
    })
    return (reverseOrder && radioButtons.reverse()) || radioButtons
  }
  return null
}

const getProductColorAndSizeAttributes = (
  list: Array<ProductAttribute>,
  isMetaStyle: boolean,
): JSX.Element | null => {
  if (list.length > 0) {
    let colorAndSize = ''

    const color = list.find((item) => item.type === 'color')
    if (color) {
      colorAndSize += color.values[0].valueLabel.toString()
    }

    const size = list.find((item) => item.type === 'size')
    if (size) {
      colorAndSize += ` ${size.typeLabel}${
        isCosBrand ? ',' : ''
      } ${size.values[0].valueLabel.toString()}`
    }

    return (
      <>
        {isArketBrand ? (
          <PreHeading className={styles.purchaseInformation}>{colorAndSize}</PreHeading>
        ) : (
          <BodyText meta={!!isMetaStyle} className={cn(isCosBrand && styles.productColourSize)}>
            {colorAndSize}
          </BodyText>
        )}
      </>
    )
  }
  return null
}

export const ProductRating = ({
  data,
  localRate,
  ratingProp,
  handleRatingClick,
  isShowRateError,
}): React.ReactElement => {
  let ratingStarSize
  switch (true) {
    case isOtherStoriesBrand:
      ratingStarSize = 'normal'
      break
    case isArketBrand:
      ratingStarSize = 'normal'
      break
    case isMonkiBrand:
      ratingStarSize = 'small'
      break
    default:
      ratingStarSize = 'small'
      break
  }

  return (
    <div
      className={cn(isArketBrand || isCosBrand ? styles.productReviewCard : styles.reviewRating)}
    >
      <figure
        className={cn(
          isArketBrand || isCosBrand ? styles.productImageContainer : styles.productImage,
        )}
      >
        <img src={data.catalogItem.imageUrl} alt={data.catalogItem.title} />
      </figure>
      <div className={cn({ [styles.productDetail]: !isArketBrand && !isCosBrand })}>
        {isOtherStoriesBrand || isCosBrand ? (
          <>
            <MetaText Tag="h2" className={styles.productTitle}>
              {data.catalogItem.title}
            </MetaText>
            {getProductColorAndSizeAttributes(data.catalogItem.attributes, isCosBrand)}
          </>
        ) : isArketBrand ? (
          <>
            <HeadingMedium Tag="h2" className={styles.productTitleAlt}>
              {data.catalogItem.title}
            </HeadingMedium>
            {getProductColorAndSizeAttributes(data.catalogItem.attributes, false)}
          </>
        ) : (
          <>
            <HeadingLarge Tag="h2">{data.catalogItem.title}</HeadingLarge>
            {getProductColorAndSizeAttributes(data.catalogItem.attributes, false)}
          </>
        )}
        {!isCosBrand ? (
          <>
            <div id={data.catalogItem.sku.toString()}>
              <RatingStars
                rating={!localRate ? ratingProp : localRate}
                className={isArketBrand ? styles.ratingStarsContainer : styles.ratingStars}
                iconSize={ratingStarSize}
                handleRatingStarOnClick={handleRatingClick}
              />
              {isMonkiBrand ? (
                <>
                  {isShowRateError && (
                    <BodyText className={styles.errorMessage}>
                      *{' '}
                      {getLabelFromStorage(
                        'profile.unreviewedproducts.submit.review.validation.rating',
                      )}
                    </BodyText>
                  )}
                </>
              ) : null}
            </div>
          </>
        ) : null}

        {!isOtherStoriesBrand ? (
          <BodyText
            className={cn(isArketBrand && styles.ratingText, isCosBrand && styles.satisfiedText)}
            meta={isCosBrand}
          >
            {getLabelFromStorage('profile.unreviewedproducts.review.page.satisfied')}
          </BodyText>
        ) : null}
        {isCosBrand &&
          renderRatingRadioList(
            localRate,
            handleRatingClick,
            window?.app?.i18n?.turnTo?.labels?.ratingLabels,
            isCosBrand,
          )}
      </div>
    </div>
  )
}
